import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Keeps a crashing PAGE from taking the console with it.
 *
 * React 19 unmounts the whole root when a render throws, so before this a
 * single malformed audit row — one row out of a real dev table, with no
 * `timestamp` — turned into a white screen: no nav, no environment banner, no
 * sign-out, nothing to click and nothing to read. The operator's only recourse
 * was to know which URL to type next.
 *
 * That is the wrong failure mode for this console specifically. It is the thing
 * you open when something has already gone wrong, so it has to degrade to "this
 * panel is broken, the rest still works" rather than to nothing at all. Mounted
 * inside the shell and keyed on the path (see Layout), so the rail, the banner
 * and the tab strip survive and navigating away clears it.
 *
 * THE FALLBACK USES NO F7. Every other surface here is built from the design
 * system, which lives on `window.F7` and is loaded at runtime — so if the thing
 * that broke IS the design system, an F7-based fallback would throw inside the
 * boundary and put us back at the white screen. Plain elements and utility
 * classes only.
 */
interface Props {
  children: ReactNode;
  /** Where this happened, for the message. */
  where?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The console has no error reporting sink, so this is the only record.
    // Kept deliberately: a white screen with a silent console is unactionable,
    // and the stack is what turns "everything disappeared" into a line number.
    console.error("Console page crashed:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <section
        role="alert"
        className="rounded-lg bg-red-50 p-5 ring-1 ring-red-500/20 dark:bg-red-400/10"
      >
        <h2 className="text-base/6 font-semibold text-red-900 dark:text-red-200">
          This page stopped rendering
        </h2>
        <p className="mt-1 text-sm/6 text-red-900/80 dark:text-red-200/80">
          {this.props.where ? `${this.props.where} hit ` : "The page hit "}
          an error partway through drawing, so it is showing nothing rather than
          something half-true. The rest of the console still works — the nav
          above is live, and moving to another page clears this.
        </p>
        <p className="mt-3 font-mono text-xs break-words text-red-900/70 dark:text-red-200/70">
          {error.message || String(error)}
        </p>
        <p className="mt-3 text-xs/5 text-red-900/70 dark:text-red-200/70">
          This is a bug in the console, not a refusal by the API and not
          something you did. The full stack is in the browser console.
        </p>
      </section>
    );
  }
}
